import React, { useState } from 'react';
import { ToastQueue } from '@adobe/react-spectrum';
import { useLocation } from 'react-router-dom';
import { generateViewStateDeepLink } from 'utils/viewStateDeepLink';
import { useViewState } from 'app/context/ViewStateContext';
import styles from './ShareViewButton.module.scss';

interface ShareViewButtonProps {
  /**
   * Label for the button (optional)
   */
  label?: string;
}

/**
 * Button to copy current view as a shareable deep link
 * Styled to match the ClearAllButton
 */
export const ShareViewButton: React.FC<ShareViewButtonProps> = ({
  label = 'Share View'
}) => {
  const getCurrentViewState = useViewState();
  const location = useLocation();
  const [isCopying, setIsCopying] = useState(false);

  const handleShareView = async () => {
    if (!getCurrentViewState) {
      return;
    }

    try {
      setIsCopying(true);

      const viewState = getCurrentViewState();

      const deepLink = generateViewStateDeepLink(viewState, location.pathname, {
        markAsSharedView: true
      });

      await navigator.clipboard.writeText(deepLink);

      ToastQueue.positive(
        'Link copied to clipboard! Anyone with this link will see the same view.',
        {
          timeout: 5000
        }
      );
    } catch (error) {
      ToastQueue.negative('Failed to copy link to clipboard', {
        timeout: 3000
      });
    } finally {
      setTimeout(() => setIsCopying(false), 300);
    }
  };

  return (
    <button
      onClick={handleShareView}
      className={styles.shareViewButton}
      aria-label={label}
      type="button"
      disabled={isCopying || !getCurrentViewState}
    >
      {label}
    </button>
  );
};
